<template>
  <div :class="$style.base">
    <BaseModal
      v-if="errorMessage"
      :message="$t(`errors.${errorMessage}`)"
      :testId="'error-modal'"
      @close="closeErrorModal"
      :unclosable="!canCloseErrorModal"
    />
    <BaseModal
      v-if="showGatewayError"
      :message="$t('errors.payment_gateway_missing')"
      @close="showGatewayError = false"
    />
    <div :class="$style.labelWrapper">
      <div :class="$style.label">
        {{ $t('confirmation.user_details') }}
      </div>

      <div :class="$style.required">*{{ $t('confirmation.required') }}</div>
    </div>
    <form
      :class="$style.form"
      @submit.prevent="onSubmit"
      v-test="'form-booking'"
    >
      <div :class="$style.inputWrap">
        <BaseFormInput
          v-model="formData.first_name"
          :label="$t('confirmation.first_name')"
          small
          required
          name="firstname"
          v-test="'first_name'"
        />
        <BaseFormInput
          v-model="formData.last_name"
          :label="$t('confirmation.last_name')"
          small
          required
          name="lastname"
          v-test="'last_name'"
        />
        <BaseFormInput
          v-model="formData.email"
          :label="$t('confirmation.email')"
          type="email"
          required
          name="email"
          :error="showEmailError ? $t('errors.email_invalid') : undefined"
          @blur="onBlur"
          v-test="'email'"
        />
        <BaseFormInput
          v-model="formData.mobile_phone"
          :label="$t('confirmation.phone')"
          type="tel"
          :isCountrySelect="true"
          required
          name="phone"
          v-test="'mobile_phone'"
        />
      </div>
      <div
        v-if="isOptionalLocation || isRequiredLocation"
        :class="$style.inputWrap"
      >
        <BaseFormInput
          v-model="formData.address"
          :label="$t('confirmation.street')"
          :required="isRequiredLocation"
          name="street"
          v-test="'address'"
        />
        <BaseFormInput
          v-model="formData.zipcode"
          :label="$t('confirmation.postcode')"
          small
          :required="isRequiredLocation"
          name="zipcode"
          v-test="'zipcode'"
        />
        <BaseFormInput
          v-model="formData.city"
          :label="$t('confirmation.city')"
          small
          :required="isRequiredLocation"
          name="city"
          v-test="'city'"
        />
      </div>
      <div :class="$style.inputWrap">
        <BaseFormInput
          v-if="
            settings.date_of_birth === 'optional' ||
            settings.date_of_birth === 'required'
          "
          v-model="formData.date_of_birth"
          :label="$t('confirmation.birthdate')"
          type="date"
          :required="settings.date_of_birth === 'required'"
          name="birthdate"
          v-test="'birthdate'"
        />
        <BaseFormInput
          v-if="widgetSettings.type === 'booking'"
          v-model="formData.notes"
          :label="$t('confirmation.comments')"
          name="comments"
          v-test="'notes'"
        />
      </div>

      <SummaryGiftcard v-if="widgetSettings.type === 'giftcard'" />
      <Summary v-else-if="widgetSettings.type === 'booking'" />

      <PaymentSelect />

      <div :class="$style.checkboxes">
        <BaseCheckbox v-model="saveUserDetails" v-test="'checkbox-userdetails'">
          {{ $t('confirmation.save_information') }}
        </BaseCheckbox>
        <BaseCheckbox
          v-if="settings.show_newsletter_optin"
          v-model="formData.enable_newsletter"
        >
          {{ $t('confirmation.newsletter_optin') }}
        </BaseCheckbox>
        <div v-if="showTermsConditions">
          <BaseCheckbox v-model="termsConditionsAccepted" required>
            {{ $t('confirmation.terms_1') }}
            <a
              :class="$style.termsLink"
              :href="settings.terms_conditions_url"
              target="_blank"
              >{{ $t('confirmation.terms_2') }}</a
            >*
          </BaseCheckbox>
          <div
            v-if="formSubmitted && !termsConditionsAccepted"
            :class="$style.error"
            v-test="'terms-conditions-error'"
          >
            {{ $t('confirmation.terms_error') }}
          </div>
        </div>
      </div>
      <div :class="$style.buttonWrap">
        <div :class="$style.privacy" v-if="settings.privacy_statement_url">
          {{ $t('confirmation.privacy_statement.label') }}
          <a
            :class="$style.termsLink"
            :href="settings.privacy_statement_url"
            target="_blank"
            >{{ $t('confirmation.privacy_statement.link.label') }}</a
          >
        </div>
        <BaseButton fullWidth :isLoading="saving" v-test="'btn-submit'">
          {{ buttonText }}
        </BaseButton>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import type { BookingData, BookingFormData, WaitingListData } from '@/types';

import axios from '@/axios';
import { computed, inject, nextTick, reactive, ref, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAvailabilityStore } from '@/stores/availability';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import widgetSettings from '@/widget-settings';

import { usePrincing } from './pricing';
import { getUserDetails, updateUserDetails } from './user-details';

import BaseButton from '@/components/BaseButton.vue';
import BaseCheckbox from '@/components/BaseCheckbox.vue';
import BaseFormInput from '@/components/base-form-input/index.vue';
import BaseModal from '@/components/BaseModal.vue';

import { provide } from 'vue';
import PaymentSelect from './PaymentSelect.vue';
import Summary from './Summary.vue';
import SummaryGiftcard from './SummaryGiftcard.vue';

const { t } = useI18n();

defineOptions({ name: 'SaveBooking' });
const emit = defineEmits(['back', 'submit']);

const gtag = inject<any>('gtag');

const { settings } = useCompanyStore();
const formData = reactive<BookingFormData>({
  address: '',
  city: '',
  date_of_birth: '',
  email: '',
  enable_newsletter: false,
  first_name: '',
  last_name: '',
  mobile_phone: '',
  notes: '',
  zipcode: ''
});

provide('bookingFormData', formData);

const submittedEmail = ref<string>('');
provide('submittedEmail', submittedEmail);

getUserDetails(formData);

const { getPrice } = usePrincing();

if (widgetSettings.type === 'booking') {
  nextTick(() => {
    getPrice({ email: formData.email });
  });
}

const isOptionalLocation = ref(settings.address_fields === 'optional');
const isRequiredLocation = ref(settings.address_fields === 'required');
const showTermsConditions = ref(!!settings.terms_conditions_url);
const termsConditionsAccepted = ref(false);
const saveUserDetails = ref(true);

const formSubmitted = ref(false);
const saving = ref(false);
const showEmailError = ref(false);

const validateEmail = (email: string) => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

const onBlur = () => {
  if (widgetSettings.type !== 'booking' || !validateEmail(formData.email)) {
    return;
  }

  getPrice({ email: formData.email }).then(() => {
    submittedEmail.value = formData.email;
  });
};

const onSubmit = () => {
  saving.value = true;
  formSubmitted.value = true;

  if (!validateEmail(formData.email)) {
    showEmailError.value = true;
    saving.value = false;
    return;
  }
  showEmailError.value = false;

  if (showTermsConditions.value && !termsConditionsAccepted.value) {
    saving.value = false;
    return;
  }

  const { waitingList, pricing } = useBookingStore();

  if (widgetSettings.mock) {
    setTimeout(() => {
      emit('submit');
      afterSave();
    }, 500);
  } else if (widgetSettings.type === 'giftcard') {
    submitGiftcard();
  } else if (widgetSettings.type === 'workshop') {
    submitWorkshop();
  } else if (waitingList) {
    submitWaitingList();
  } else {
    submitBooking();
  }

  if (pricing?.quote) {
    gtag.trackEvent('booking_confirm', {
      amount: pricing.quote.total / 100
    });
  }
};

const showGatewayError = ref(false);

const submitWorkshop = () => {
  const { workshopId } = useBookingStore();

  const data = {
    ...formData,
    workshop_id: workshopId
  };

  window.parent.postMessage(
    {
      name: 'workshopSubmitted',
      data: toRaw(data)
    },
    '*'
  );

  axios
    .post(`/companies/${widgetSettings.company}/workshops/register`, data)
    .then(
      ({
        data: {
          workshop_attendee: { status, payment_url }
        }
      }) => {
        if (status === 'payment_required' && payment_url) {
          const navWindow = window.top ?? window;
          navWindow.location.href = payment_url;
          return;
        }

        emit('submit');
      }
    )
    .catch(({ response: { data } }) => {
      handleBookingErrors(data.errors);
    })
    .finally(() => {
      afterSave();
    });
};

const submitGiftcard = () => {
  const { company } = useCompanyStore();
  const { giftcardAmount } = useBookingStore();

  window.parent.postMessage(
    {
      name: 'giftCardSubmitted',
      data: toRaw(formData)
    },
    '*'
  );

  axios
    .post(`/companies/${company.id}/giftcard_purchases`, {
      ...formData,
      amount: giftcardAmount
    })
    .then(({ data: { giftcard_purchase } }) => {
      if (window.top && giftcard_purchase.payment_url) {
        window.top.location.href = giftcard_purchase.payment_url;
      }
    })
    .catch(
      ({
        response: {
          data: { errors }
        }
      }) => {
        if (errors?.base.length) {
          showGatewayError.value = true;
        }
      }
    )
    .finally(() => {
      saving.value = false;
    });
};

const submitBooking = () => {
  const {
    locationId,
    serviceIds,
    date,
    time,
    employeeId,
    timeSlotPriceAdjustment,
    discountCode,
    giftcardCode,
    giftcardRedeemedAmount,
    paymentMethod,
    pricing
  } = useBookingStore();

  const fullyPaidWithGiftcard =
    pricing?.quote?.total === giftcardRedeemedAmount;

  const paymentOption = fullyPaidWithGiftcard
    ? 'pay_at_location'
    : settings.prepayments_for_new_customers_only &&
        settings.online_payment !== 'optional'
      ? 'unknown'
      : paymentMethod;

  const bookingData: BookingData = {
    ...formData,
    date,
    last_minute_discount_percentage: -timeSlotPriceAdjustment,
    discount_code: discountCode?.code || '',
    giftcard_id:
      giftcardCode?.id && giftcardRedeemedAmount ? giftcardCode.id : null,
    giftcard_redeem_amount: giftcardRedeemedAmount || null,
    applied_discount_code_secret: discountCode?.secret || '',
    payment_option: paymentOption,
    resource_id: employeeId,
    rwg_token: widgetSettings.rwgToken,
    source: widgetSettings.source,
    service_ids: toRaw(serviceIds),
    time,
    widget_id: widgetSettings.widgetId
  };

  window.parent.postMessage(
    {
      name: 'bookingSubmitted',
      data: toRaw(bookingData)
    },
    '*'
  );

  axios
    .post(`/locations/${locationId}/bookings`, bookingData)
    .then(({ data: { booking } }) => {
      if (booking.payment_url) {
        const navWindow = window.top ?? window;
        navWindow.location.href = booking.payment_url;
        return;
      }

      if (settings.success_url && window.top) {
        window.top.location.href = settings.success_url;
      } else {
        const bookingStore = useBookingStore();
        bookingStore.bookingResponse = booking;
        emit('submit');
      }
    })
    .catch(({ response: { data } }) => {
      handleBookingErrors(data.errors);
    })
    .finally(() => {
      afterSave();
    });
};

const submitWaitingList = () => {
  const { locationId, serviceIds, date, employeeId, daysOfWeek, partsOfDay } =
    useBookingStore();

  const bookingData: WaitingListData = {
    ...formData,
    date: !daysOfWeek.length ? date : null,
    days_of_week: daysOfWeek.length ? toRaw(daysOfWeek) : [],
    parts_of_day: daysOfWeek.length ? toRaw(partsOfDay) : [],
    resource_id: employeeId,
    service_ids: toRaw(serviceIds)
  };

  window.parent.postMessage(
    {
      name: 'waitingListSubmitted',
      data: toRaw(bookingData)
    },
    '*'
  );

  axios
    .post(`/locations/${locationId}/waiting_list_entries`, bookingData)
    .then(() => {
      emit('submit');
    })
    .catch(({ response: { data } }) => {
      handleBookingErrors(data.errors);
    })
    .finally(() => {
      afterSave();
    });
};

const afterSave = () => {
  saving.value = false;

  updateUserDetails(formData, saveUserDetails.value);
};

const { resetAvailabilityStore } = useAvailabilityStore();

const formErrors = ref<{ [key: string]: string[] }>();
const errorMessage = computed(() => {
  const error =
    formErrors.value?.start_at ||
    formErrors.value?.resource ||
    formErrors.value?.customer ||
    formErrors.value?.mobile_phone ||
    formErrors.value?.discount_code;

  if (error) {
    return error[0];
  }
  const arrayError = formErrors.value?.[0];

  if (arrayError) {
    return arrayError.type;
  }
});

const canCloseErrorModal = computed(() => {
  const error =
    formErrors.value?.start_at ||
    formErrors.value?.resource ||
    formErrors.value?.customer;
  if (!error) {
    return true;
  }
  return error[0] !== 'blocked';
});

const handleBookingErrors = (errors: { [key: string]: string[] }) => {
  if (!errors) {
    return;
  }

  formErrors.value = errors;
};

const closeErrorModal = () => {
  if (!formErrors.value) {
    return;
  }

  if (formErrors.value.start_at || formErrors.value.resource) {
    resetAvailabilityStore();
    emit('back');
  }

  formErrors.value = undefined;
};

const buttonText = computed(() => {
  const { requestBooking } = useCompanyStore();
  const { waitingList, pricing, paymentMethod } = useBookingStore();

  if (widgetSettings.type !== 'giftcard' && requestBooking) {
    return t('confirmation.request');
  }

  if (widgetSettings.type !== 'giftcard' && waitingList) {
    return t('waiting_list.confirm');
  }

  if (
    widgetSettings.type === 'giftcard' ||
    pricing?.quote?.payment_strategy === 'percentage' ||
    pricing?.quote?.payment_strategy === 'required' ||
    (pricing?.quote?.payment_strategy === 'optional' &&
      paymentMethod === 'pay_online')
  ) {
    return t('confirmation.pay_now');
  }

  if (widgetSettings.type === 'workshop') {
    return t('confirmation.book_workshop');
  }

  return t('confirmation.confirm');
});
</script>

<style lang="scss" module>
.base {
  @include page;
  padding: $spacing $spacing 0;
  background-color: $color-bg;

  &:after {
    content: '';
    display: block;
    height: $spacing;
  }
}

.inputWrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.labelWrapper {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.label {
  margin-bottom: $spacing;
  font-weight: bold;
}

.required {
  @include font-sub;
  font-size: 12px;
}

.termsLink {
  @include link;
}

.checkboxes {
  & > *:not(:last-child) {
    margin-bottom: $spacing * 0.5;
  }
}

.error {
  color: $color-error;
  font-size: 13px;
  margin-top: 4px;
}

.buttonWrap {
  margin-top: $spacing * 2;
}

.privacy {
  font-size: 12px;
  margin-bottom: 0.5 * $spacing;
}
</style>
